import { useCallback, useState } from 'react';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Props for the CapabilityListField component
 */
interface CapabilityListFieldProps {
  /** The current list of capability strings */
  capabilities: string[];
  /** Called with the next list whenever capabilities are added or removed */
  onChange: (next: string[]) => void;
  /** Optional validation error message to display */
  error?: string;
}

/**
 * Editor for a list of opaque capability strings (e.g. "source:flat-file").
 *
 * Capabilities are entered one at a time and rendered as removable badges.
 * Duplicates and blank entries are ignored; the Auth Service stores the strings
 * verbatim, so no format validation is enforced here.
 */
export function CapabilityListField({ capabilities, onChange, error }: CapabilityListFieldProps) {
  const [draft, setDraft] = useState('');

  /**
   * Add the current draft capability to the list.
   */
  const addCapability = useCallback(() => {
    const value = draft.trim();
    if (!value || capabilities.includes(value)) {
      setDraft('');
      return;
    }
    onChange([...capabilities, value]);
    setDraft('');
  }, [draft, capabilities, onChange]);

  /**
   * Remove a capability from the list.
   */
  const removeCapability = useCallback(
    (value: string) => {
      onChange(capabilities.filter((c) => c !== value));
    },
    [capabilities, onChange]
  );

  return (
    <div className="grid gap-2">
      <div className="flex gap-2">
        <Input
          aria-label="Capability string"
          placeholder="e.g. source:flat-file"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter adds the capability without submitting the surrounding form.
            if (e.key === 'Enter') {
              e.preventDefault();
              addCapability();
            }
          }}
        />
        <Button type="button" variant="secondary" onClick={addCapability} disabled={!draft.trim()}>
          Add
        </Button>
      </div>

      {capabilities.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {capabilities.map((capability) => (
            <Badge key={capability} variant="secondary" className="gap-1">
              {capability}
              <button
                type="button"
                aria-label={`Remove ${capability}`}
                onClick={() => removeCapability(capability)}
                className="rounded-sm hover:text-destructive"
              >
                <X />
              </button>
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No capabilities added yet.</p>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
